import { createGlobalStore } from "@wss/store";
import { useEffect } from "react";
import { PlusSubscription } from "~/my-account/Plus/plusSubscription";
import { AlertType } from "../ExpiringCreditCards/AlertTypes";
import usePlusSubscriptions from "./usePlusSubscriptions";

const findSoonestExpiringCard = (
  subscriptions: PlusSubscription[],
): PlusSubscription | null => {
  if (subscriptions.length === 0) {
    return null;
  }

  return subscriptions.reduce((soonest, current) => {
    const soonestExpirationDate = new Date(soonest?.cardExpiration ?? 0);
    const currentExpirationDate = new Date(current.cardExpiration);
    return currentExpirationDate < soonestExpirationDate ? current : soonest;
  }, subscriptions[0] ?? null);
};

const findSoonestRenewalCard = (
  subscriptions: PlusSubscription[],
): PlusSubscription | null => {
  if (subscriptions.length === 0) return null;

  const soonest = subscriptions.reduce((soonest, current) => {
    const soonestRenewalDate = new Date(soonest?.renewalDate ?? 0);
    const currentRenewalDate = new Date(current.renewalDate);
    return currentRenewalDate < soonestRenewalDate ? current : soonest;
  }, subscriptions[0] ?? null);

  return soonest;
};

const findCurrentlyExpiredCard = (
  subscriptions: PlusSubscription[],
): PlusSubscription | null => {
  if (subscriptions.length === 0) {
    return null;
  }

  const expiredCard = subscriptions.find((subscription) => {
    const expirationDate = new Date(subscription.cardExpiration);
    expirationDate.setDate(expirationDate.getDate() + 1);
    const isExpired = expirationDate <= new Date();

    return isExpired;
  });

  return expiredCard || null;
};

const normalizeDate = (date: Date) => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0); // Set time to midnight
  return normalizedDate;
};

const hasMultipleCurrentlyExpired = (
  multipleCurrentlyExpiredCards: PlusSubscription[] | null,
  currentDate: Date,
) => {
  if (
    multipleCurrentlyExpiredCards?.length &&
    multipleCurrentlyExpiredCards.length > 1 &&
    multipleCurrentlyExpiredCards[0]?.renewalDate
  ) {
    const renewalDate = new Date(multipleCurrentlyExpiredCards[0].renewalDate);
    const dayAfterRenewal = new Date(renewalDate);
    dayAfterRenewal.setDate(renewalDate.getDate() + 1);

    // Normalize the dayAfterRenewal date
    const normalizedDayAfterRenewal = normalizeDate(dayAfterRenewal);

    // Calculate the end date, 14 days after the renewal date
    const endDate = new Date(renewalDate);
    endDate.setDate(renewalDate.getDate() + 14);

    // Normalize the endDate to midnight
    const normalizedEndDate = normalizeDate(endDate);

    // Normalize the currentDate to midnight
    const normalizedCurrentDate = normalizeDate(currentDate);

    return (
      normalizedCurrentDate >= normalizedDayAfterRenewal &&
      normalizedCurrentDate <= normalizedEndDate
    );
  }
  return false;
};

const hasSingleCurrentlyExpired = (
  currentlyExpiredCard: PlusSubscription | null,
  currentDate: Date,
) => {
  if (currentlyExpiredCard) {
    const renewalDate = new Date(currentlyExpiredCard.renewalDate);
    const dayAfterRenewal = new Date(renewalDate);
    dayAfterRenewal.setDate(renewalDate.getDate() + 1);

    const normalizedDayAfterRenewal = normalizeDate(dayAfterRenewal);

    const endDate = new Date(renewalDate);
    endDate.setDate(renewalDate.getDate() + 14);

    const normalizedEndDate = normalizeDate(endDate);

    const normalizedCurrentDate = normalizeDate(currentDate);

    return (
      normalizedCurrentDate >= normalizedDayAfterRenewal &&
      normalizedCurrentDate <= normalizedEndDate
    );
  }
  return false;
};

const hasPlusRenewalInNextWeekWithExpiredCard = (
  soonestRenewalCard: PlusSubscription | null,
  currentDate: Date,
  oneWeekFromNow: Date,
) => {
  if (soonestRenewalCard) {
    const cardExpirationDate = new Date(soonestRenewalCard.cardExpiration);
    cardExpirationDate.setDate(cardExpirationDate.getDate() + 1);
    const renewalDate = new Date(soonestRenewalCard.renewalDate);

    return (
      cardExpirationDate <= currentDate &&
      renewalDate >= currentDate &&
      renewalDate <= oneWeekFromNow
    );
  }
  return false;
};

const calculateAlertType = (
  multipleCardsExpiringThisMonth: boolean,
  soonestExpiringCard: PlusSubscription | null,
  soonestRenewalCard: PlusSubscription | null,
  multipleCardsExpiringThisWeek: boolean,
  currentlyExpiredCard: PlusSubscription | null,
  multipleCurrentlyExpiredCards: PlusSubscription[] | null,
): AlertType | null => {
  const currentDate = new Date();
  const oneWeekFromNow = new Date();
  oneWeekFromNow.setDate(currentDate.getDate() + 7);

  // Check for multiple currently expired cards
  if (hasMultipleCurrentlyExpired(multipleCurrentlyExpiredCards, currentDate)) {
    return "MultipleCurrentlyExpired";
  }

  // Check for a single currently expired card
  if (hasSingleCurrentlyExpired(currentlyExpiredCard, currentDate)) {
    return "CurrentlyExpired";
  }

  if (
    hasPlusRenewalInNextWeekWithExpiredCard(
      soonestRenewalCard,
      currentDate,
      oneWeekFromNow,
    )
  ) {
    return multipleCardsExpiringThisWeek ? "WeekMulti" : "WeekSingle";
  }

  if (!soonestExpiringCard) {
    return null;
  }

  if (!multipleCardsExpiringThisMonth) {
    return "MonthSingle";
  }

  if (multipleCardsExpiringThisMonth) {
    return "MonthMulti";
  }

  return null;
};

const getPlusAlertType = (
  subscriptions: PlusSubscription[],
): AlertType | null => {
  const currentDate = new Date();
  const oneMonthFromNow = new Date(currentDate);
  oneMonthFromNow.setMonth(currentDate.getMonth() + 1);
  const oneWeekFromNow = new Date(currentDate);
  oneWeekFromNow.setDate(currentDate.getDate() + 7);

  const expiringCardsNextMonth = subscriptions.filter((subscription) => {
    const expiry = new Date(subscription.cardExpiration);
    expiry.setDate(expiry.getDate() + 1);
    return (
      expiry >= currentDate &&
      expiry <= oneMonthFromNow &&
      subscription.status === "Active"
    );
  });

  const expiringCardsNextWeek = subscriptions.filter((subscription) => {
    const renewalDate = new Date(subscription.renewalDate);
    const expirationDate = new Date(subscription.cardExpiration);
    expirationDate.setDate(expirationDate.getDate() + 1);
    const isExpiringNextWeek =
      renewalDate >= currentDate && renewalDate <= oneWeekFromNow;
    const isExpiredPaymentMethod = expirationDate <= currentDate;

    return (
      isExpiringNextWeek &&
      isExpiredPaymentMethod &&
      subscription.status === "Active"
    );
  });

  const multipleCurrentlyExpiredCards = subscriptions.filter((subscription) => {
    const expirationDate = new Date(subscription.cardExpiration);
    expirationDate.setDate(expirationDate.getDate() + 1);
    return expirationDate <= currentDate;
  });

  const uniqueExpiringCardsNextMonth = new Set(
    expiringCardsNextMonth.map((card) => card.paymentMethodId),
  );

  const uniqueExpiringCardsNextWeek = new Set(
    expiringCardsNextWeek.map((card) => card.paymentMethodId),
  );

  const currentlyExpiredCard = findCurrentlyExpiredCard(subscriptions);

  const alertType = calculateAlertType(
    uniqueExpiringCardsNextMonth.size > 1,
    findSoonestExpiringCard(expiringCardsNextMonth),
    findSoonestRenewalCard(expiringCardsNextWeek),
    uniqueExpiringCardsNextWeek.size > 1,
    currentlyExpiredCard,
    multipleCurrentlyExpiredCards.length > 1
      ? multipleCurrentlyExpiredCards
      : null,
  );

  return alertType;
};

export type PlusAlertResult = {
  alert: AlertType | null;
  status: "complete" | "loading" | "idle";
};

export const plusPaymentAlertStore = createGlobalStore<PlusAlertResult>(() => ({
  status: "idle",
  alert: null,
}));

/**
 * Custom React hook for fetching the plus payment alert type, if any.
 *
 * @param guid Unique identifier of user
 * @returns {{ alert: AlertType | null}}
 */
export const usePlusPaymentAlert = (guid: string) => {
  const plusData = usePlusSubscriptions(guid);

  useEffect(() => {
    if (
      plusPaymentAlertStore.getState().status === "idle" &&
      plusData.status === "complete"
    ) {
      plusPaymentAlertStore.setState({ status: "loading" });
      const alertType = getPlusAlertType(plusData.subscriptions);
      plusPaymentAlertStore.setState({
        status: "complete",
        alert: alertType,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plusData.status, guid]);

  return plusPaymentAlertStore.useState();
};

export default usePlusPaymentAlert;
